import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';

const W = 480;
const H = 290;
const GRAVITY = 0.55;
const JUMP_FORCE = -11.5;
const SPEED = 3.5;
const WORLD_W = 2800;

// Gaps = "Scope Creep Pits" / ground = "Sprint Lanes"
const PLATFORMS = [
  [0,   240, 480, 50, 'ground', 'Sprint 1'],
  [550, 240, 420, 50, 'ground', 'Sprint 2'],
  [1050,240, 500, 50, 'ground', 'Sprint 3'],
  [1650,240, 460, 50, 'ground', 'Sprint 4'],
  [2200,240, 600, 50, 'ground', 'MVP 🚀'],
  // Floating — "Roadmap Items"
  [180, 175,  96, 16, 'brick', ''],
  [350, 140,  80, 16, 'brick', ''],
  [600, 175, 112, 16, 'brick', ''],
  [760, 130,  80, 16, 'brick', ''],
  [920, 170,  96, 16, 'brick', ''],
  [1100,195, 112, 16, 'brick', ''],
  [1260,150,  80, 16, 'brick', ''],
  [1420,185,  96, 16, 'brick', ''],
  [1700,170, 112, 16, 'brick', ''],
  [1870,125,  80, 16, 'brick', ''],
  [2030,175, 112, 16, 'brick', ''],
  [2250,160,  96, 16, 'brick', ''],
  [2430,120,  80, 16, 'brick', ''],
  [2580,165, 112, 16, 'brick', ''],
];

// Coins = "User Stories" (tickets)
const COIN_POS = [
  [190,150],[220,150],[250,150],
  [360,115],[390,115],
  [610,150],[645,150],[680,150],
  [770,105],[800,105],
  [930,145],[960,145],
  [1110,170],[1145,170],[1180,170],
  [1270,125],[1300,125],
  [1710,145],[1745,145],
  [1880,100],[1910,100],
  [2040,150],[2075,150],[2110,150],
  [2260,135],[2295,135],
  [2440,95],[2470,95],
  [2590,140],[2625,140],[2660,140],
];

const FLAG_X = 2680;

// PM-themed death messages
const DEATH_MSGS = [
  'Fell into the Scope Creep Pit.\nBacklog reprioritised. Again.',
  'Blocked by dependencies.\nHave you tried a meeting about it?',
  'Fell off the roadmap.\nWas never officially approved anyway.',
  'Dropped into Tech Debt.\nEstimated fix: next quarter.',
  'Missed the sprint gap.\nVelocity: 0. Excuses: many.',
];

// PM-themed win messages based on score
function winMsg(score, total) {
  if (score === total) return 'All user stories accepted! 🎉\nStakeholders are satisfied.\n(First time in recorded history.)';
  if (score >= total * 0.8) return 'Ship it! 80%+ stories done.\nThe rest is "out of scope".\n(You planned this.)';
  if (score >= total * 0.5) return 'Reached MVP.\n50% of features shipped.\nCalling it a "phased rollout".';
  return 'You made it to prod.\nOnly ' + score + ' stories done.\nCalling it a "soft launch".';
}

function initState() {
  return {
    px: 60, py: 190, vx: 0, vy: 0, onGround: false, facing: 1,
    camX: 0,
    coins: COIN_POS.map((c, i) => ({ x: c[0], y: c[1], id: i, done: false })),
    score: 0,
    phase: 'playing',
    deathMsg: DEATH_MSGS[0],
  };
}

// Pixel-art Mario character
function drawMario(ctx, x, y, facing, isJumping) {
  ctx.save();
  if (facing === -1) {
    ctx.translate((x + 18) * 2, 0);
    ctx.scale(-1, 1);
  }
  const p = (col, row, w = 1, h = 1, color) => {
    ctx.fillStyle = color;
    ctx.fillRect(x + col * 4, y + row * 4, w * 4, h * 4);
  };
  p(1, 0, 4, 1, '#E52521');
  p(0, 1, 6, 1, '#E52521');
  p(1, 1, 1, 1, '#FBBF77');
  p(0, 2, 1, 1, '#5B3825');
  p(1, 2, 4, 1, '#FBBF77');
  p(5, 2, 1, 1, '#5B3825');
  p(1, 3, 1, 1, '#5B3825');
  p(2, 3, 1, 1, '#000');
  p(3, 3, 1, 1, '#FBBF77');
  p(4, 3, 1, 1, '#000');
  p(5, 3, 1, 1, '#5B3825');
  p(1, 4, 4, 1, '#5B3825');
  p(0, 5, 6, 1, '#E52521');
  p(0, 5, 1, 1, '#FBBF77');
  p(5, 5, 1, 1, '#FBBF77');
  p(1, 5, 4, 1, '#0052A5');
  p(1, 6, 1, 2, '#0052A5');
  p(2, 6, 2, 2, '#E52521');
  p(4, 6, 1, 2, '#0052A5');
  p(0, 7, 1, 1, '#0052A5');
  p(5, 7, 1, 1, '#0052A5');
  p(isJumping ? 1 : 0, 8, 2, 1, '#5B3825');
  p(isJumping ? 3 : 4, 8, 2, 1, '#5B3825');
  ctx.restore();
}

export default function MarioGame({ onClose }) {
  const canvasRef = useRef(null);
  const stateRef = useRef(initState());
  const keysRef = useRef({});
  const jumpedRef = useRef(false);
  const animRef = useRef(null);
  const [uiScore, setUiScore] = useState(0);
  const [phase, setPhase] = useState('playing');
  const [deathMsg, setDeathMsg] = useState('');

  const restart = useCallback(() => {
    stateRef.current = initState();
    setUiScore(0);
    setPhase('playing');
    setDeathMsg('');
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    function onKeyDown(e) {
      keysRef.current[e.code] = true;
      if (['Space','ArrowUp','KeyW','ArrowLeft','ArrowRight','KeyA','KeyD'].includes(e.code))
        e.preventDefault();
      if ((e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW') && !jumpedRef.current) {
        jumpedRef.current = true;
        const s = stateRef.current;
        if (s.onGround && s.phase === 'playing') {
          s.vy = JUMP_FORCE;
          s.onGround = false;
        }
      }
    }
    function onKeyUp(e) {
      keysRef.current[e.code] = false;
      if (['Space','ArrowUp','KeyW'].includes(e.code)) jumpedRef.current = false;
    }
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    function update() {
      const s = stateRef.current;
      if (s.phase !== 'playing') return;
      const k = keysRef.current;

      if (k['ArrowLeft'] || k['KeyA'])       { s.vx = -SPEED; s.facing = -1; }
      else if (k['ArrowRight'] || k['KeyD']) { s.vx = SPEED;  s.facing = 1;  }
      else s.vx *= 0.75;

      s.vy += GRAVITY;
      s.px += s.vx;
      s.py += s.vy;
      s.px = Math.max(0, Math.min(s.px, WORLD_W - 36));
      s.onGround = false;

      for (const [px, py, pw] of PLATFORMS) {
        if (s.px + 32 > px && s.px + 4 < px + pw &&
            s.py + 36 > py && s.py + 36 < py + 12 && s.vy >= 0) {
          s.py = py - 36; s.vy = 0; s.onGround = true;
        }
      }

      if (s.py > H + 80) {
        s.deathMsg = DEATH_MSGS[Math.floor(Math.random() * DEATH_MSGS.length)];
        s.phase = 'dead';
        setDeathMsg(s.deathMsg);
        setPhase('dead');
      }

      for (const c of s.coins) {
        if (!c.done && Math.abs(s.px + 18 - c.x) < 22 && Math.abs(s.py + 18 - c.y) < 22) {
          c.done = true; s.score++; setUiScore(s.score);
        }
      }

      if (s.px + 18 > FLAG_X) { s.phase = 'win'; setPhase('win'); }

      s.camX = Math.max(0, Math.min(s.px - W / 3, WORLD_W - W));
    }

    function draw() {
      const s = stateRef.current;
      const cam = s.camX;

      // Sky
      const sky = ctx.createLinearGradient(0, 0, 0, H);
      sky.addColorStop(0, '#5BB8F5');
      sky.addColorStop(1, '#92D4F8');
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, W, H);

      // Clouds
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      [[80,28],[260,16],[500,38],[760,20],[1020,32]].forEach(([cx, cy]) => {
        const rx = ((cx - cam * 0.25) % (W + 120) + W + 120) % (W + 120) - 60;
        ctx.beginPath();
        ctx.arc(rx, cy, 18, 0, Math.PI*2);
        ctx.arc(rx+20, cy-8, 14, 0, Math.PI*2);
        ctx.arc(rx+40, cy, 18, 0, Math.PI*2);
        ctx.fill();
      });

      // Platforms
      for (const [px, py, pw, ph, type, label] of PLATFORMS) {
        const sx = px - cam;
        if (sx + pw < 0 || sx > W) continue;
        if (type === 'ground') {
          // Grass
          ctx.fillStyle = '#5BAD2A';
          ctx.fillRect(sx, py, pw, 10);
          // Dirt
          ctx.fillStyle = '#8B4513';
          ctx.fillRect(sx, py + 10, pw, ph - 10);
          // Brick lines
          ctx.strokeStyle = 'rgba(0,0,0,0.12)'; ctx.lineWidth = 1;
          for (let bx = 0; bx < pw; bx += 32) ctx.strokeRect(sx + bx, py + 10, 32, 18);
          // Sprint label
          if (label) {
            ctx.fillStyle = 'rgba(255,255,255,0.85)';
            ctx.font = 'bold 9px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(label, sx + pw / 2, py + 24);
            ctx.textAlign = 'left';
          }
          // Gap labels — "SCOPE CREEP" between ground chunks
        } else {
          // Roadmap brick — purple/teal PM flavor
          ctx.fillStyle = '#7C4DFF';
          ctx.fillRect(sx, py, pw, ph);
          ctx.fillStyle = '#9C6FFF';
          ctx.fillRect(sx, py, pw, 3);
          ctx.strokeStyle = 'rgba(0,0,0,0.2)'; ctx.lineWidth = 1;
          for (let bx = 0; bx < pw; bx += 24) ctx.strokeRect(sx + bx, py, 24, ph);
        }
      }

      // Gap labels "SCOPE CREEP ⚠️"
      const gaps = [[480,550],[970,1050],[1550,1650],[2060,2200]];
      for (const [g0, g1] of gaps) {
        const gx = ((g0 + g1) / 2) - cam;
        if (gx < -80 || gx > W + 80) continue;
        ctx.fillStyle = 'rgba(255,60,60,0.7)';
        ctx.font = 'bold 8px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('SCOPE CREEP ⚠', gx, 258);
        ctx.textAlign = 'left';
      }

      // Flag → "SHIPPED! 🚀" pole
      const fx = FLAG_X - cam;
      if (fx > -40 && fx < W + 40) {
        ctx.fillStyle = '#888';
        ctx.fillRect(fx, 100, 5, 140);
        ctx.fillStyle = '#00C853';
        ctx.fillRect(fx + 5, 100, 38, 24);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 8px Arial';
        ctx.fillText('SHIP', fx + 7, 116);
        ctx.fillStyle = '#ff4';
        ctx.beginPath(); ctx.arc(fx + 2.5, 100, 8, 0, Math.PI * 2); ctx.fill();
      }

      // User Stories (tickets) — blue sticky notes with ✓
      for (const c of s.coins) {
        if (c.done) continue;
        const cx = c.x - cam;
        if (cx < -20 || cx > W + 20) continue;
        // Sticky note
        ctx.fillStyle = '#1565C0';
        ctx.fillRect(cx - 9, c.y - 9, 18, 18);
        ctx.fillStyle = '#1E88E5';
        ctx.fillRect(cx - 9, c.y - 9, 18, 4);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 10px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('✓', cx, c.y + 4);
        ctx.textAlign = 'left';
      }

      // Player
      drawMario(ctx, s.px - cam, s.py, s.facing, !s.onGround);

      // HUD
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(6, 6, 175, 24);
      ctx.fillStyle = '#69F0AE';
      ctx.font = 'bold 12px "Lucida Console", monospace';
      ctx.fillText(`STORIES: ${s.score}/${COIN_POS.length}`, 12, 23);
    }

    function loop() {
      update(); draw();
      animRef.current = requestAnimationFrame(loop);
    }
    animRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  return (
    <Div>
      <div className="mg-header">
        <div className="mg-logo">
          <span className="mg-logo-super">SUPER</span>
          <span className="mg-logo-pm">PM</span>
          <span className="mg-logo-bros">BROS.</span>
        </div>
        <span className="mg-controls">← → Navigate Backlog &nbsp;|&nbsp; ↑ Escalate to Leadership</span>
      </div>
      <div className="mg-wrap">
        <canvas ref={canvasRef} width={W} height={H} />
        {phase === 'dead' && (
          <Overlay>
            <div className="ov-title">SPRINT FAILED</div>
            <div className="ov-sub">Stories closed: {uiScore} / {COIN_POS.length}</div>
            <div className="ov-msg">{deathMsg}</div>
            <button className="ov-btn" onClick={restart}>↩ Replan Sprint</button>
          </Overlay>
        )}
        {phase === 'win' && (
          <Overlay win>
            <div className="ov-title">🚀 SHIPPED TO PROD!</div>
            <div className="ov-sub">Stories: {uiScore} / {COIN_POS.length}</div>
            <div className="ov-msg">{winMsg(uiScore, COIN_POS.length)}</div>
            <button className="ov-btn" onClick={restart}>▶ Next Quarter</button>
          </Overlay>
        )}
      </div>
    </Div>
  );
}

const Div = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  background: #1a1a2e;
  font-family: 'Lucida Console', monospace;

  .mg-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 12px;
    background: #111;
    flex-shrink: 0;
    border-bottom: 3px solid #7C4DFF;
  }
  .mg-logo {
    display: flex;
    align-items: baseline;
    gap: 5px;
  }
  .mg-logo-super {
    font-size: 10px;
    font-weight: bold;
    color: #fff;
    letter-spacing: 2px;
    font-family: Arial, sans-serif;
  }
  .mg-logo-pm {
    font-size: 20px;
    font-weight: 900;
    color: #7C4DFF;
    letter-spacing: 1px;
    font-family: Arial Black, sans-serif;
    -webkit-text-stroke: 1.5px #69F0AE;
    text-shadow: 2px 2px 0 #000, -1px -1px 0 #000;
  }
  .mg-logo-bros {
    font-size: 12px;
    font-weight: bold;
    color: #69F0AE;
    letter-spacing: 1px;
    font-family: Arial, sans-serif;
    text-shadow: 1px 1px 0 #000;
  }
  .mg-controls {
    font-size: 9px;
    color: rgba(255,255,255,0.45);
    font-family: 'Lucida Console', monospace;
  }
  .mg-wrap {
    position: relative;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #000;
  }
  canvas {
    display: block;
    image-rendering: pixelated;
    image-rendering: crisp-edges;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.82);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #fff;
  text-align: center;
  font-family: 'Lucida Console', monospace;
  padding: 20px;

  .ov-title {
    font-size: 22px;
    font-weight: bold;
    color: ${({ win }) => win ? '#69F0AE' : '#FF5252'};
    text-shadow: 2px 2px 0 #000;
    letter-spacing: 2px;
  }
  .ov-sub {
    font-size: 12px;
    color: #7C4DFF;
    font-weight: bold;
  }
  .ov-msg {
    font-size: 11px;
    color: rgba(255,255,255,0.75);
    white-space: pre-line;
    line-height: 1.7;
    max-width: 340px;
  }
  .ov-btn {
    margin-top: 8px;
    background: #7C4DFF;
    color: #fff;
    border: 2px solid #69F0AE;
    padding: 7px 24px;
    font-size: 12px;
    font-family: 'Lucida Console', monospace;
    font-weight: bold;
    cursor: pointer;
    letter-spacing: 1px;
    &:hover { background: #9C6FFF; }
    &:active { transform: translateY(1px); }
  }
`;
